import { useDisclosure } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { analytics } from 'analytics';
import Menu from 'ui/Menu';
import { toaster } from 'ui/Toaster';
import Tooltip from 'ui/Tooltip';
import { formToSankey } from 'utils/form';

import { useApiContext } from '../../../contexts/apiContext';
import { useSankeyFormContext } from '../../../contexts/sankeyFormContext';
import { Path, useCurrentRouteSankeyId } from '../../../utils/routes';
import { isProtectedSankey } from '../../../utils/sankey';
import { useCurrentUser } from '../../../utils/user';
import DeleteDiagramModal from '../../DeleteDiagramModal';
import TrackedMenuItem from '../TrackedMenuItem';

const DeleteDiagramMenuItem: React.FC = () => {
  const { api } = useApiContext();
  const { refetchUser } = useCurrentUser();
  const { isReadOnly, form } = useSankeyFormContext();
  const currentSankeyId = useCurrentRouteSankeyId();
  const disclosure = useDisclosure();
  const navigate = useNavigate();

  const isProtected = isProtectedSankey(formToSankey(form));

  const deleteMutation = useMutation(async () => {
    try {
      // Delete the diagram and refetch user to make sure permissions are up to date
      await api().sankeys.destroy({ id: currentSankeyId });
      await refetchUser();
      disclosure.onClose();
      toaster.success({ title: 'Diagram deleted successfully' });
      navigate(Path.Sankeys);
    } catch (error) {
      analytics.reportError('Error deleting the diagram', { error });
    }
  });

  if (isProtected) {
    return (
      <Tooltip content="Important diagrams can only be deleted through Django admin UI">
        <Menu.Item disabled value="delete-diagram" isDanger>
          Delete this diagram
        </Menu.Item>
      </Tooltip>
    );
  }

  return (
    <>
      <TrackedMenuItem
        disabled={isReadOnly}
        onClick={disclosure.onOpen}
        value="delete-diagram"
        isDanger
      >
        Delete this diagram
      </TrackedMenuItem>
      <DeleteDiagramModal
        isLoading={deleteMutation.isLoading}
        onConfirm={deleteMutation.mutate}
        {...disclosure}
      />
    </>
  );
};

export default DeleteDiagramMenuItem;
